/**
 * marginSage — the ledger figure that belongs beside each of the four margin cards.
 *
 * WHY. Our cards come from the ERP invoice; Sage is the finance ledger. They share the invoice
 * date, so they must agree, and a reader should be able to see whether they do WITHOUT leaving
 * the card row. Four Sage rows map one-to-one onto the four cards, which is the whole reason
 * this is cheap: Revenue against Value Generated, COGS against Cost of Production, Gross Profit
 * against Gross Margin, GPM against GM%.
 *
 * ⚠ THE TWO WINDOWS MUST BE THE SAME ONE. The cards cover closed INVOICE months and the Sage
 * summary covers closed LEDGER months, and those are separately computed. Comparing figures
 * from different spans would manufacture a variance out of the calendar, so `compare` refuses
 * to produce a subtitle at all unless the caller confirms the spans match.
 *
 * ⚠ AND ONE PAIR IS NOT LIKE FOR LIKE, by construction. Our Gross Margin is taken on the
 * COSTED value; Sage's Gross Profit is taken on all of its revenue. At Kirkstall that is a
 * 97% population against a 100% one, at Oracle 66%. The delta is still worth showing — it is
 * small at Kirkstall and the reason it is large at Oracle is the finding — but the expansion
 * beneath the cards is where that gets explained, not in six words of small print.
 *
 * Pure functions, no fetch (CD-7).
 */

import { pick, WINDOW_PERIOD } from './margin'
import { formatCurrency } from '../utils/formatters'
import type { Cell, Site } from '../types/metrics'
import type { SageSubs } from '../components/OrderBookTiles'

const gbp = (v: number): string => formatCurrency(v, '£', 0)

/** A signed delta with a real minus sign (U+2212), not a hyphen — the platform sets its
 * numerals that way everywhere else, and a hyphen reads as a dash mid-sentence. */
const signed = (v: number): string =>
  `${v > 0 ? '+' : v < 0 ? '\u2212' : ''}${Math.abs(v).toFixed(1)}`

const value = (cell: Cell | null): number | null =>
  cell && cell.value !== null ? cell.value : null

/** The four ledger figures for this site over the window, or nulls where Sage served none. */
export interface SageWindow {
  revenue: number | null
  cogs: number | null
  grossProfit: number | null
  /** Sage's own GPM, served as a ratio (0.307), not a percentage. */
  gpm: number | null
}

/** No ledger at all — every leg an em-dash rather than a zero. */
export const EMPTY_SAGE: SageWindow = { revenue: null, cogs: null, grossProfit: null, gpm: null }

export function sageWindow(lookup: Map<string, Cell>, site: Site): SageWindow {
  return {
    revenue: value(pick(lookup, 'revenue', site, WINDOW_PERIOD)),
    cogs: value(pick(lookup, 'cogs', site, WINDOW_PERIOD)),
    grossProfit: value(pick(lookup, 'gross_profit', site, WINDOW_PERIOD)),
    gpm: value(pick(lookup, 'gpm', site, WINDOW_PERIOD)),
  }
}

/** "Sage £2,762,878 · +3.1%" — theirs, then how far ours sits from it. Null when either side
 * is absent: a variance against a figure Sage never posted is not a variance. */
function line(ours: number | null, theirs: number | null): string | undefined {
  if (ours === null || theirs === null || theirs === 0) return undefined
  return `Sage ${gbp(theirs)} · ${signed((100 * (ours - theirs)) / theirs)}%`
}

/** The percentage pair, compared in POINTS. A percentage change of a percentage is the kind of
 * number nobody can act on — 31.5% against 30.7% is 0.8 points, not 2.6%. */
function pointLine(oursPct: number | null, theirsRatio: number | null): string | undefined {
  if (oursPct === null || theirsRatio === null) return undefined
  const theirs = theirsRatio * 100
  return `Sage ${theirs.toFixed(1)}% · ${signed(oursPct - theirs)}pt`
}

/**
 * The four subtitles, or nothing at all.
 *
 * `comparable` is the caller's confirmation that the cards and the ledger cover the SAME span.
 * It is a parameter rather than something derived here because only the caller holds both
 * spans, and a silently mismatched comparison is worse than none.
 */
export function compare(
  tiles: { value: number; cost: number | null; gm: number | null; gmPct: number | null },
  sage: SageWindow,
  comparable: boolean
): SageSubs {
  // ⚠ ALWAYS AN OBJECT, never undefined. Its presence is what tells the card strip it answers
  // to a ledger, and that is what suppresses the per-card explanatory notes. A window we
  // cannot compare must still not put "816 lines · billed" back on the card — it just has
  // nothing to say in that slot.
  if (!comparable) return {}
  return {
    value: line(tiles.value, sage.revenue),
    cost: line(tiles.cost, sage.cogs),
    gm: line(tiles.gm, sage.grossProfit),
    gmPct: pointLine(tiles.gmPct, sage.gpm),
  }
}
